/**
 * LA FRÉQUENCE D'OUVERTURE DE LA PAGE « DONNÉES DU CAPTEUR » (DEV-2071).
 * ============================================================================
 *
 * CE QUE LE CLIENT DOIT VIVRE. Il ouvre la page des données d'une source et
 * voit tout de suite des courbes. Jamais un écran dont chaque graphique dit
 * « La fréquence sélectionnée n'est pas compatible avec ce graphique » alors
 * qu'aucun n'a été choisi par lui.
 *
 * CE QUI SE PASSAIT. La page s'ouvrait toujours en « Donnée brute » (`raw`),
 * quelle que soit la source. Sur un compteur Strasbourg sans courbe de charge
 * (graphiques : consommation daily → monthly, puissance max daily), AUCUN
 * graphique affiché ne propose `raw` : les deux cartes s'ouvraient sur le
 * message d'incompatibilité (vu le 23/09 sur staging).
 *
 * LA RÈGLE (générique, aucun type de source nommé ici) :
 *
 *  1. la fréquence par défaut est la PLUS FINE que propose au moins un des
 *     graphiques sélectionnés ET pour laquelle la source a des données ;
 *  2. une fréquence courante (choisie par l'utilisateur ou venue de l'URL) est gardée
 *     tant qu'au moins un graphique sélectionné la propose ; sinon on bascule
 *     sur celle de la règle 1.
 *
 * « A des données à la fréquence f » se lit dans ce que dapi sert déjà au
 * front (`informations.<type>.data`) : une variable a des données à `f` si elle
 * a une première date à `f` ou à un pas plus FIN (dapi agrège vers le haut, il
 * n'invente pas vers le bas). Une variable dont on ne sait rien ne contraint
 * pas : faute de preuve, on ne retire rien.
 *
 * Pas de message ajouté (règle de Mathieu du 08/09, pas de bannières) : la page
 * choisit simplement une fréquence que ses graphiques savent afficher.
 */
import type { Freq } from "@modules/data/types/graph"

/** Les pas connus, du plus fin au plus grossier. */
export const ORDRE_DES_PAS: readonly Freq[] = [
    "raw",
    "hourly",
    "daily",
    "weekly",
    "monthly",
    "yearly",
]

function rang(pas: string): number {
    const i = ORDRE_DES_PAS.indexOf(pas as Freq)
    return i === -1 ? Number.POSITIVE_INFINITY : i
}

/** Ce qu'il faut savoir d'un graphique sélectionné pour choisir sa fréquence. */
export type GraphiquePourFrequence = {
    /** Fréquences que le graphique affiché sait tracer. */
    freq: readonly Freq[]
    /** Variables tracées (préfixe du `dataset.type`, avant le point). */
    variables: readonly string[]
}

/** Pas détenus par variable : `{ energie_elec_tout: ["daily", "hourly"] }`. */
export type PasDetenus = Record<string, readonly string[]>

function aUnePremiereDate(slot: unknown): boolean {
    return (
        !!slot &&
        typeof slot === "object" &&
        (slot as { date_from?: unknown }).date_from != null
    )
}

function pasAvecDonnees(bloc: Record<string, unknown>): string[] {
    return ORDRE_DES_PAS.filter((pas) => aUnePremiereDate(bloc[pas]))
}

/**
 * Lit les pas détenus par variable dans `informations.<type>.data` tel que
 * dapi le sert, sous ses deux formes connues :
 *  - `data._variables: [{ type, raw: {date_from}, hourly: {…}, … }]` ;
 *  - `data.<variable>.<pas>.date_from` (forme Strasbourg, DEV-421).
 *
 * @param informations - `source.informations` (toutes clés de type confondues)
 * @returns Les pas détenus par variable, ou `null` si la source n'en dit rien
 */
export function pasDetenusDeLaSource(
    informations: Record<string, unknown> | null | undefined
): PasDetenus | null {
    if (!informations || typeof informations !== "object") return null
    const resultat: Record<string, Set<string>> = {}
    const ajoute = (variable: string, pas: string[]) => {
        if (!variable || pas.length === 0) return
        resultat[variable] ??= new Set()
        pas.forEach((p) => resultat[variable].add(p))
    }

    for (const infoType of Object.values(informations)) {
        const data = (infoType as { data?: unknown } | null)?.data
        if (!data || typeof data !== "object") continue
        const variables = (data as { _variables?: unknown })._variables
        if (Array.isArray(variables)) {
            for (const v of variables) {
                if (!v || typeof v !== "object") continue
                ajoute(
                    String((v as { type?: unknown }).type ?? ""),
                    pasAvecDonnees(v as Record<string, unknown>)
                )
            }
        }
        for (const [cle, bloc] of Object.entries(data)) {
            if (cle.startsWith("_") || !bloc || typeof bloc !== "object")
                continue
            if (Array.isArray(bloc)) continue
            ajoute(cle, pasAvecDonnees(bloc as Record<string, unknown>))
        }
    }

    const variables = Object.keys(resultat)
    if (variables.length === 0) return null
    return Object.fromEntries(variables.map((v) => [v, [...resultat[v]]]))
}

/**
 * Le graphique a-t-il de quoi s'afficher à `pas` ?
 * Oui si l'une de ses variables CONNUES a une donnée à ce pas ou plus fin ;
 * oui aussi si aucune de ses variables n'est connue (on ne retire rien sans
 * preuve).
 */
function aDesDonneesA(
    graphique: GraphiquePourFrequence,
    pas: Freq,
    pasDetenus: PasDetenus | null
): boolean {
    if (!pasDetenus) return true
    const connues = graphique.variables.filter((v) => v in pasDetenus)
    if (connues.length === 0) return true
    return connues.some((v) =>
        pasDetenus[v].some((detenu) => rang(detenu) <= rang(pas))
    )
}

/**
 * Choisit la fréquence de la page.
 *
 * @param params.courante - Fréquence choisie (URL, mémoire) ; `null` = aucune
 * @param params.frequencesPage - Fréquences offertes par le sélecteur de la page
 * @param params.graphiques - Graphiques sélectionnés (graphique affiché de chaque carte)
 * @param params.pasDetenus - Pas détenus par variable (`null` = inconnu)
 * @returns La fréquence à appliquer
 */
export function choisirFrequence(params: {
    courante: Freq | null
    frequencesPage: readonly string[]
    graphiques: readonly GraphiquePourFrequence[]
    pasDetenus: PasDetenus | null
}): Freq | null {
    const { courante, frequencesPage, graphiques, pasDetenus } = params
    // Sans graphique sélectionné, rien ne permet de juger : on ne touche à rien.
    if (graphiques.length === 0) return courante

    const candidates = [...frequencesPage]
        .filter((f) => rang(f) !== Number.POSITIVE_INFINITY)
        .sort((a, b) => rang(a) - rang(b)) as Freq[]

    const proposee = (f: Freq) => graphiques.some((g) => g.freq.includes(f))

    // Règle 2 : une fréquence courante proposée par au moins un graphique reste.
    if (courante && candidates.includes(courante) && proposee(courante)) {
        return courante
    }

    // Règle 1 : la plus fine proposée ET avec des données.
    const avecDonnees = candidates.find((f) =>
        graphiques.some(
            (g) => g.freq.includes(f) && aDesDonneesA(g, f, pasDetenus)
        )
    )
    if (avecDonnees) return avecDonnees

    // Repli : la plus fine simplement proposée, sinon on ne change rien.
    return candidates.find(proposee) ?? courante
}
